import React, {useState, useEffect} from 'react'
import {SplitFactory} from '@splitsoftware/splitio'
import cookie from 'js-cookie'
import {Analytics, Datalayer} from '@miro-site/analytics'
import {Attributes} from '@splitsoftware/splitio/types/splitio'

let anonymousIdInterval: number

declare global {
  interface Window {
    split?: SplitIO.ISDK
  }
}

enum STATUSES {
  loading = 'loading',
  ready = 'READY',
  error = 'ERROR',
}

type State = {
  status: STATUSES
  error?: string
  isLoading: boolean
}

type Actions = SplitIO.IClient | null | undefined

type SplitIoContextType = [State, Actions]

const initialValue: State = {status: STATUSES.loading, isLoading: true}

const SplitIoContext = React.createContext<SplitIoContextType>([initialValue, null])

interface SplitIoProviderProps {
  authorizationKey: string
  getAnalytics: () => Promise<Analytics>
  triesLimit?: number
  children: React.ReactNode
}

export const SplitIoProvider = (props: SplitIoProviderProps): React.ReactElement => {
  const {authorizationKey, children, getAnalytics, triesLimit = 20} = props

  const [splitIOState, setSplitIOState] = useState<Omit<State, 'isLoading'>>(initialValue)
  const [client, setClient] = useState<Actions>(null)

  function isBot() {
    const userAgent = navigator.userAgent
    const regexp = /bot|crawl|slurp|spider|mediapartners|go-http-client|apache|axios|ahc/i
    return !!(userAgent && userAgent.match(regexp))
  }

  useEffect(() => {
    if (isBot()) {
      setSplitIOState({status: STATUSES.error, error: 'Crawler / bot detected'})
    } else {
      setSplitIOState({status: STATUSES.loading, error: ''})

      let tries = 0
      anonymousIdInterval = window.setInterval(() => {
        if (tries >= triesLimit) {
          setSplitIOState({status: STATUSES.error, error: `Id cookie wasn't found`})
          clearInterval(anonymousIdInterval)
          return
        }

        const anonymousId = cookie.get('ajs_anonymous_id')
        if (typeof anonymousId === 'string' && anonymousId.length) {
          clearInterval(anonymousIdInterval)
          initSplitIo(anonymousId.replace(/"/g, ''))
        }

        tries++
      }, 50)
    }

    async function initSplitIo(anonymousId: string) {
      let analytics: Analytics

      try {
        analytics = await getAnalytics()
      } catch (e) {
        setSplitIOState({status: STATUSES.error, error: `Analytics library wasn't initialized`})
        return
      }

      // Init Split on client-side
      ;(function () {
        window.split =
          window.split ||
          SplitFactory({
            core: {
              authorizationKey,
              key: anonymousId,
            },
            impressionListener: {
              logImpression(impression) {
                const {changeNumber, feature, treatment, label} = impression.impression
                // Make sure to connect this in GTM, otherwise events will not land on EventHub
                analytics.sendEvent(
                  new Datalayer('experiment_viewed', {
                    experiment_id: `${feature}#${changeNumber}`,
                    experiment_name: feature,
                    variation_id: treatment,
                    variation_name: treatment,
                    target: label,
                    data_source: 'web-monorepo-frontend',
                  }),
                )
              },
            },
          })
      })()

      // This branch runs only on the client-side
      const client = window.split.client()
      await client.ready()

      try {
        await client.ready().catch(e => {
          throw e
        })

        setSplitIOState({status: STATUSES.ready, error: ''})
        setClient(client)
      } catch (e) {
        setSplitIOState({status: STATUSES.error, error: `Split IO wasn't initialized`})
        setClient(client)
      }
    }
  }, [])

  return (
    <SplitIoContext.Provider
      value={[{...splitIOState, isLoading: splitIOState.status == STATUSES.loading}, client]}
    >
      {children}
    </SplitIoContext.Provider>
  )
}

export const useSplitIo = (): SplitIoContextType => React.useContext(SplitIoContext)

export const useTreatment = (name: string, attrs?: Attributes): [string | undefined, boolean] => {
  const [{isLoading}, client] = useSplitIo()

  return [client?.getTreatment(name, attrs), isLoading]
}
